(() => {
  if (customElements.get('mojis-cart-drawer')) return;
  const make = (tag, className, text) => { const node = document.createElement(tag); if (className) node.className = className; if (text !== undefined) node.textContent = text; return node; };
  const localPath = value => {
    const url = new URL(value || '', location.origin);
    if (url.origin !== location.origin || !/\/(?:cart|cart\/change(?:\.js)?)$/.test(url.pathname)) throw new Error('Bag route is unavailable.');
    // Liquid routes.cart_change_url is /cart/change on Shopify, not the .js
    // fixture path. Preserve the locale prefix and normalize the Ajax endpoint.
    return url.pathname.endsWith('/cart/change') ? url.pathname + '.js' : url.pathname;
  };
  const syncPdpCartCount = (link, quantity) => {
    link.setAttribute('aria-label', 'Cart, ' + quantity + ' items');
    let bubble = link.querySelector('.cart-count-bubble');
    if (!bubble) {
      bubble = make('div', 'cart-count-bubble');
      const count = make('span'); count.setAttribute('aria-hidden', 'true');
      bubble.append(count, make('span', 'visually-hidden'));
      link.append(bubble);
    }
    bubble.querySelector('span[aria-hidden="true"]').replaceChildren(String(quantity));
    bubble.querySelector('.visually-hidden').replaceChildren(quantity + ' items');
    bubble.hidden = quantity === 0;
  };
  customElements.define('mojis-cart-drawer', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.listeners = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.pending = false;
      this.querySelectorAll('[data-bag-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), {signal:this.listeners.signal}));
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, {signal:this.listeners.signal});
      this.dialog.addEventListener('close', () => {
        document.body.style.overflow = this.previousOverflow || '';
        if (this.restoreFocus?.isConnected) this.restoreFocus.focus();
        this.restoreFocus = null;
      }, {signal:this.listeners.signal});
      this.querySelector('[data-bag-refresh]').addEventListener('click', async () => {
        if (this.pending) return;
        this.pending = true;
        try { this.render(await this.read()); this.status('Bag refreshed. Review your items before continuing.'); this.querySelector('[data-bag-refresh]').hidden = true; }
        catch (_) { this.status('Could not refresh your bag. Use View bag or try refreshing again.'); }
        finally { this.pending = false; }
      }, {signal:this.listeners.signal});
      window.addEventListener('click', event => {
        const viewBag = event.target.closest('.mojis-bag-dialog__view');
        if (!viewBag || !this.contains(viewBag) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (localPath(viewBag.href) !== localPath(this.dataset.cartUrl)) return;
        event.preventDefault();
        event.stopPropagation();
        location.assign(viewBag.href);
      }, {capture:true, signal:this.listeners.signal});
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-bag-change]');
        if (button) this.change(button);
      }, {signal:this.listeners.signal});
      document.addEventListener('click', event => {
        const link = event.target.closest('.mojis-store-cart-link, .mojis-header a[href]');
        if (!link || this.contains(link) || new URL(link.href).pathname.replace(/\/$/, '') !== this.dataset.cartUrl) return;
        event.preventDefault();
        this.open().catch(() => location.assign(link.href));
      }, {signal:this.listeners.signal});
    }
    disconnectedCallback() {
      this.listeners?.abort();
      if (this.dialog?.open) {
        this.dialog.close();
        document.body.style.overflow = this.previousOverflow || '';
      }
      this.initialized = false;
    }
    status(text) { this.querySelector('[data-bag-status]').textContent = text; }
    async request(path, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(path, { credentials:'same-origin', cache:'no-store', ...options, signal:controller.signal, headers:{ Accept:'application/json', ...options.headers } });
        const cart = await response.json();
        if (!response.ok || cart.status || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || !Number.isSafeInteger(cart.total_price)) throw new Error(cart.description || 'Could not update your bag.');
        return cart;
      } finally { clearTimeout(timer); }
    }
    read() { return this.request(localPath(this.dataset.cartUrl) + '.js'); }
    render(cart) {
      this.cart = cart;
      const currency = typeof cart.currency === 'string' ? cart.currency : 'USD';
      const format = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style:'currency', currency }).format(cents / 100);
      const items = this.querySelector('[data-bag-items]');
      items.replaceChildren();
      cart.items.forEach(item => {
        const row = make('div', 'mojis-bag-dialog__line');
        const image = item.featured_image?.url || item.image;
        if (image) {
          const img = make('img'); img.src = image; img.alt = ''; img.width = 72; img.height = 72; img.loading = 'lazy'; row.append(img);
        }
        const info = make('div', 'mojis-bag-dialog__info');
        const title = make('a', '', item.product_title || item.title || 'Cart item');
        const target = new URL(item.url || this.dataset.cartUrl, location.origin);
        title.href = target.origin === location.origin ? target.pathname + target.search : this.dataset.cartUrl;
        info.append(title);
        if (item.variant_title && item.variant_title !== 'Default Title') info.append(make('p', '', item.variant_title));
        Object.entries(item.properties || {}).filter(([name, value]) => !name.startsWith('_') && value).forEach(([name, value]) => info.append(make('p', '', name + ': ' + value)));
        if (item.selling_plan_allocation?.selling_plan?.name) info.append(make('p', '', item.selling_plan_allocation.selling_plan.name));
        const controls = make('div', 'mojis-bag-dialog__controls');
        const rule = item.quantity_rule || { min:1, increment:1 };
        const step = rule.increment || 1;
        for (const [label, quantity, text] of [['Decrease', item.quantity - step, '−'], ['Increase', item.quantity + step, '+'], ['Remove', 0, 'Remove']]) {
          const button = make('button', '', text); button.type = 'button'; button.dataset.bagChange = item.key; button.dataset.quantity = quantity; button.dataset.previous = item.quantity;
          button.setAttribute('aria-label', label + ' ' + title.textContent + ' quantity');
          button.disabled = label === 'Decrease' && quantity < (rule.min || 1) || label === 'Increase' && rule.max && quantity > rule.max;
          controls.append(button);
          if (label === 'Decrease') controls.append(make('span', '', String(item.quantity)));
        }
        info.append(controls);
        const price = make('div', 'mojis-bag-dialog__price');
        price.append(make('strong', '', format(item.final_line_price ?? item.price * item.quantity)));
        if (item.original_line_price > item.final_line_price) price.append(make('s', '', format(item.original_line_price)));
        (item.line_level_discount_allocations || []).forEach(discount => price.append(make('small', '', discount.discount_application?.title || 'Discount')));
        row.append(info, price); items.append(row);
      });
      if (!cart.items.length) items.append(make('p', '', 'Your bag is empty.'));
      this.querySelector('[data-bag-subtotal]').textContent = format(cart.total_price);
      (cart.cart_level_discount_applications || []).forEach(discount => items.append(make('p', '', discount.title + ': −' + format(discount.total_allocated_amount))));
      document.querySelectorAll('.mojis-store-cart-link').forEach(link => {
        link.setAttribute('aria-label', 'Cart, ' + cart.item_count + ' items');
        let count = link.querySelector('span');
        if (!count) { count = make('span'); link.append(' ', count); }
        count.textContent = String(cart.item_count); count.hidden = cart.item_count === 0;
      });
      document.querySelectorAll('.mojis-header #cart-icon-bubble').forEach(link => syncPdpCartCount(link, cart.item_count));
      this.status(cart.item_count + (cart.item_count === 1 ? ' item' : ' items') + ' in your bag');
    }
    async change(button) {
      if (this.pending) return;
      this.pending = true;
      this.querySelectorAll('[data-bag-change]').forEach(node => { node.disabled = true; });
      this.status('Updating your bag…');
      try {
        const fresh = await this.read();
        const line = fresh.items.find(item => item.key === button.dataset.bagChange);
        if (!line || line.quantity !== Number(button.dataset.previous)) { this.render(fresh); this.status('Your bag changed elsewhere. Review it and choose again.'); return; }
        if (!this.isConnected) return;
        const result = await this.request(localPath(this.dataset.changeUrl), { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ id:line.key, quantity:Number(button.dataset.quantity) }) });
        if (!this.isConnected) return;
        const updated = result.items.find(item => String(item.id) === String(line.id) && JSON.stringify(item.properties || {}) === JSON.stringify(line.properties || {}) && String(item.selling_plan_allocation?.selling_plan?.id || '') === String(line.selling_plan_allocation?.selling_plan?.id || ''));
        const desired = Number(button.dataset.quantity);
        if (desired === 0 ? !!updated : updated?.quantity !== desired) throw new Error('The response did not confirm this update.');
        this.render(result);
        this.querySelector('[data-bag-close]').focus();
      } catch (error) {
        this.status(error.name === 'AbortError' || error instanceof TypeError ? 'The update response was interrupted. Refresh your bag before trying again.' : error.message + ' Refresh your bag before trying again.');
        this.querySelector('[data-bag-refresh]').hidden = false;
      } finally { this.pending = false; }
    }
    async open() {
      if (this.dialog.open || this.pending) return true;
      this.pending = true;
      try {
        const cart = await this.read();
        if (!this.isConnected) return false;
        this.render(cart);
        this.restoreFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        this.previousOverflow = document.body.style.overflow;
        this.dialog.showModal();
        document.body.style.overflow = 'hidden';
        this.querySelector('[data-bag-close]').focus();
        return true;
      } finally { this.pending = false; }
    }
  });
})();
